import * as React from 'react';
import type {Selection} from '@react-types/shared';
import type {FormAdapter, FormFieldValue} from '../useFormRegister';

const isReactEvent = (
  event: unknown,
): event is React.ChangeEvent<HTMLElement> => {
  return !!event && typeof event === 'object' && 'target' in event;
};

const isSelection = (value: unknown): value is Selection => {
  return !!value && typeof value === 'object' && 'size' in value;
};

type SelectFieldValues = FormFieldValue | Selection;

type ChangeAdapter = FormAdapter<
  FormFieldValue,
  SelectFieldValues
>['changeAdapter'];

type SelectFieldFormAdapter = {
  valueAdapter: (
    fieldValue: FormFieldValue | SelectFieldValues,
    controlValue?: FormFieldValue | SelectFieldValues,
  ) => SelectFieldValues;
  changeAdapter: (
    eventOrValue: React.ChangeEvent<HTMLElement> | SelectFieldValues,
    fieldValue: FormFieldValue,
    controlValue?: FormFieldValue,
  ) => FormFieldValue;
};

/**
 * A hook that provides adapter functions for SelectField form integration.
 *
 * Handles the transformation between form values (arrays) and SelectField values (Selection objects)
 * for multiple selection mode. For single selection, values pass through unchanged.
 */
export function useSelectFieldFormAdapter(
  isMultiSelect: boolean,
): SelectFieldFormAdapter {
  const multiSelectValueAdapter: FormAdapter<SelectFieldValues>['valueAdapter'] =
    React.useCallback((fieldValue) => {
      if (fieldValue instanceof Set) {
        return fieldValue;
      }
      if (Array.isArray(fieldValue)) {
        return new Set(fieldValue);
      }
      return new Set(fieldValue ? [String(fieldValue)] : []);
    }, []);

  const multiSelectChangeAdapter: ChangeAdapter = React.useCallback(
    (eventOrValue, fieldValue) => {
      if (isSelection(eventOrValue)) {
        return Array.from(eventOrValue).map(String);
      }
      if (Array.isArray(eventOrValue)) {
        return eventOrValue;
      }
      return Array.isArray(fieldValue) ? fieldValue : [];
    },
    [],
  );

  const singleSelectChangeAdapter: ChangeAdapter = React.useCallback(
    (eventOrValue, fieldValue) => {
      if (isReactEvent(eventOrValue)) {
        return fieldValue;
      }

      // This won't happen in single selection, we just make TS happy.
      if (eventOrValue instanceof Set) {
        return fieldValue;
      }

      return eventOrValue;
    },
    [],
  );

  return React.useMemo(
    () => ({
      valueAdapter: isMultiSelect
        ? multiSelectValueAdapter
        : (eventOrValue) => eventOrValue,
      changeAdapter: isMultiSelect
        ? multiSelectChangeAdapter
        : singleSelectChangeAdapter,
    }),
    [
      isMultiSelect,
      multiSelectValueAdapter,
      multiSelectChangeAdapter,
      singleSelectChangeAdapter,
    ],
  );
}
